import { useEffect, useRef, useState } from 'react';
import { Input, Radio, Checkbox, Button, Space, Typography, Alert, Divider, theme as antdTheme } from 'antd';
import { HolderOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { space } from '@viq-admin/ui';
import { reorderById } from '../../utils/reorder';
import { DiscoveryQuestion, DiscoveryQuestionType } from '../../api/types';
import { QuestionPayload } from '../../api/discovery';

interface DraftOption {
  key: string;
  text: string;
}

export interface QuestionFormProps {
  initial?: DiscoveryQuestion | null;
  /** Inventory editor shows the "default" checkbox; the conference editor hides it. */
  showDefault?: boolean;
  submitting?: boolean;
  onSubmit: (payload: QuestionPayload) => void | Promise<void>;
  onCancel: () => void;
  /**
   * Hides this form's own Cancel button. Set by the portal `QuestionDetail`
   * bare-form embedding (`QuestionEdit`) — the shared `DetailPage` already
   * renders a single Cancel in its header (routed through its discard
   * guard), so a second Cancel button here would both be redundant and, more
   * importantly, would bypass that guard entirely if clicked. The standalone
   * `QuestionCreate`/`QuestionEdit`-as-a-page callers leave this unset.
   */
  hideCancel?: boolean;
  /**
   * Fires `true` the first time ANY field changes after mount (never back to
   * `false` — mirrors the antd `Form`'s `onValuesChange` "touched" semantics
   * the other bare edit forms use). `QuestionForm` has no antd `<Form>` under
   * it (it's a hand-rolled stateful component), so this is the only signal
   * the embedding `DetailPage`'s discard guard has for "has this session been
   * edited". Not called for the standalone Create/Edit-as-page callers (they
   * don't pass it).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const MAX_OPTIONS = 12;

function SortableOption({
  option,
  onChange,
  onRemove,
}: {
  option: DraftOption;
  onChange: (text: string) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: option.key });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const { token } = antdTheme.useToken();
  return (
    <div ref={setNodeRef} style={{ ...style, display: 'flex', alignItems: 'center', gap: space.sm, marginBottom: space.sm }}>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', color: token.colorTextQuaternary }} aria-label="Drag to reorder">
        <HolderOutlined />
      </span>
      <Input
        placeholder="Option text"
        value={option.text}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button type="text" aria-label="Remove option" icon={<CloseOutlined />} onClick={onRemove} />
    </div>
  );
}

export function QuestionForm({
  initial,
  showDefault = false,
  submitting,
  onSubmit,
  onCancel,
  hideCancel = false,
  onDirtyChange,
}: QuestionFormProps) {
  const seq = useRef(0);
  const mkKey = () => `opt-${seq.current++}`;

  const [text, setText] = useState(initial?.text ?? '');
  const [type, setType] = useState<DiscoveryQuestionType>(initial?.type ?? 'choice');
  const [allowMultiple, setAllowMultiple] = useState(initial?.allowMultiple ?? false);
  const [hasOtherOption, setHasOtherOption] = useState(initial?.hasOtherOption ?? false);
  const [required, setRequired] = useState(initial?.required ?? false);
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [options, setOptions] = useState<DraftOption[]>(
    initial?.options?.length
      ? initial.options.map((o) => ({ key: mkKey(), text: o.text }))
      : [{ key: mkKey(), text: '' }, { key: mkKey(), text: '' }],
  );
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  // Skip the mount-time run (initial state seeded from `initial`, not a user
  // edit) — every render after that reports dirty. Never resets to `false`
  // here; a fresh session gets a fresh mount (and thus a fresh ref) because
  // the embedding DetailPage unmounts editView entirely on exit from edit.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onDirtyChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, type, allowMultiple, hasOtherOption, required, isDefault, options]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    setOptions((prev) => reorderById(prev, String(active.id), String(over.id), (o) => o.key));
  }

  function addOption() {
    setOptions((prev) => (prev.length >= MAX_OPTIONS ? prev : [...prev, { key: mkKey(), text: '' }]));
  }

  function handleSave() {
    setError(null);
    if (!text.trim()) {
      setError('Question text is required');
      return;
    }
    if (type === 'choice') {
      const clean = options.map((o) => o.text.trim()).filter(Boolean);
      if (clean.length < 2) {
        setError('Choice questions need at least 2 options');
        return;
      }
      if (clean.length > MAX_OPTIONS) {
        setError(`Choice questions allow at most ${MAX_OPTIONS} options`);
        return;
      }
      void onSubmit({
        text: text.trim(),
        type,
        allowMultiple,
        hasOtherOption,
        required,
        ...(showDefault ? { isDefault } : {}),
        options: clean.map((t) => ({ text: t })),
      });
      return;
    }
    void onSubmit({
      text: text.trim(),
      type,
      required,
      ...(showDefault ? { isDefault } : {}),
    });
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Text strong>Type</Typography.Text>
        <div>
          <Radio.Group value={type} onChange={(e) => setType(e.target.value)} style={{ marginTop: space.xs }}>
            <Radio value="choice">Choice</Radio>
            <Radio value="free_text">Free-text</Radio>
          </Radio.Group>
        </div>
      </div>

      <label style={{ display: 'block' }}>
        <Typography.Text strong>Question</Typography.Text>
        <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} style={{ marginTop: space.xs }} />
      </label>

      {type === 'choice' && (
        <Checkbox checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)}>
          Allow multiple answers
        </Checkbox>
      )}
      <Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)}>Required</Checkbox>
      {showDefault && (
        <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}>
          Add to new conferences by default
        </Checkbox>
      )}

      {type === 'choice' ? (
        <div>
          <Divider style={{ margin: '8px 0' }} />
          <Typography.Text strong>Answer options </Typography.Text>
          <Typography.Text type="secondary">(2–12 · drag to reorder)</Typography.Text>
          <div style={{ marginTop: space.sm }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={options.map((o) => o.key)} strategy={verticalListSortingStrategy}>
                {options.map((o) => (
                  <SortableOption
                    key={o.key}
                    option={o}
                    onChange={(t) => setOptions((prev) => prev.map((p) => (p.key === o.key ? { ...p, text: t } : p)))}
                    onRemove={() => setOptions((prev) => prev.filter((p) => p.key !== o.key))}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Button size="small" icon={<PlusOutlined />} onClick={addOption} disabled={options.length >= MAX_OPTIONS}>
              Add option
            </Button>
          </div>
          <Checkbox checked={hasOtherOption} onChange={(e) => setHasOtherOption(e.target.checked)} style={{ marginTop: space.md }}>
            Include an "Other: ___" free-text option
          </Checkbox>
        </div>
      ) : (
        <Alert
          type="info"
          message="No options. The rep types a free-form answer on the tear sheet — ideal for grabbing a cell phone, a referral name, or a one-off detail."
        />
      )}

      {error && <Alert type="error" message={error} />}

      <Space>
        <Button type="primary" loading={submitting} onClick={handleSave}>Save</Button>
        {!hideCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Space>
    </Space>
  );
}
